import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Linking, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens.ts';
import { RouteNavigationParams } from 'router/types';

import { setFullscreen } from 'store/epg/actions';
import { selectEpgPlayerFullscreen } from 'store/epg/selectors';
import { hidePlayer, setModalPlayerFullscreen } from 'store/player/actions';
import { selectPlayerConfig } from 'store/player/selectors';
import { logout, setSuspensionType } from 'store/user/actions';
import { SUSPENSION_TYPES } from 'store/user/reducer.types';
import {
    selectIfCollectionSuspensionActive,
    selectSuspension,
    selectUserAuthData,
} from 'store/user/selectors';

import { COLOR } from 'utils/colors';
import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import useCollectionSuspension from 'utils/hooks/useCollectionSuspension';
import useEntitlementsIntervalUpdate from 'utils/hooks/useEntitlementsIntervalUpdate';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { isTablet, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import { onAfterHidePlayerModal } from 'screens/PlayerModal/utils';

import DialogView from 'components/Dialog';
import Spinner from 'components/Spinner';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

const {
    MY_ACCOUNT: {
        SUSPENSION_VOLUNTARY_NOTIFICATION_TITLE,
        SUSPENSION_VOLUNTARY_NOTIFICATION_BODY,
        SUSPENSION_COLLECTION_NOTIFICATION_TITLE,
        SUSPENSION_COLLECTION_NOTIFICATION_BODY,
        SUSPENSION_COLLECTION_OVER_NOTIFICATION_TITLE,
        SUSPENSION_COLLECTION_OVER_NOTIFICATION_DESCRIPTION,
    },
    NO_NETWORK_CONNECTION: { TRY_AGAIN_BUTTON },
    RELOAD_BUTTON,
    REBOOT_BUTTON,
    SELFCARE_APP_BUTTON,
    SUBSCRIBE_OVERLAY_SELFCARE_LINK,
} = LANGUAGE_KEYS;

export default function SuspensionAlert() {
    const { t } = useTranslation();
    const edges: Edge[] = useSafeAreaEdges();
    const dispatch = useDispatch();
    const navigation =
        useNavigation<StackNavigationProp<RouteNavigationParams>>();

    useCollectionSuspension();
    useEntitlementsIntervalUpdate();

    const suspension = useSelector(selectSuspension);
    const authData = useSelector(selectUserAuthData);
    const { visible: fullscreenPlayerVisible } =
        useSelector(selectPlayerConfig);
    const epgPlayerInFullscreen = useSelector(selectEpgPlayerFullscreen);
    const collectionSuspensionActive = useSelector(
        selectIfCollectionSuspensionActive,
    );

    const [visible, setVisible] = useState(true);
    const [loading, setLoading] = useState(false);

    const { onEnterTheApp } = useEnterTheApp();

    const handleReloadApp = useCallback(async () => {
        try {
            setLoading(true);
            await onEnterTheApp();
            dispatch(setSuspensionType(undefined));
            setLoading(false);
        } catch (error) {
            // Even if onEnterTheApp fail - open PROFILES screen to don't block the user.
            navigation.reset({ routes: [{ name: SCREEN.PROFILES }] });
        }
    }, [navigation, onEnterTheApp]);

    const handleTryAgain = useCallback(() => {
        dispatch(setSuspensionType(undefined));
    }, []);

    const handleOpenSelfcareApp = useCallback(() => {
        Linking.openURL(t(SUBSCRIBE_OVERLAY_SELFCARE_LINK));
    }, [t]);

    const handleCloseFullscreenPlayer = useCallback(() => {
        if (fullscreenPlayerVisible) {
            onAfterHidePlayerModal();
            dispatch(hidePlayer());

            if (isWeb) {
                dispatch(setModalPlayerFullscreen(false));
            }
        } else if (epgPlayerInFullscreen) {
            onAfterHidePlayerModal();
            dispatch(setFullscreen(false));
        }
    }, [fullscreenPlayerVisible, epgPlayerInFullscreen]);

    useEffect(() => {
        if (suspension === SUSPENSION_TYPES.VOLUNTARY) {
            setVisible(true);

            if (authData) {
                handleCloseFullscreenPlayer();
                dispatch(logout(navigation));
            }
        } else if (suspension === SUSPENSION_TYPES.COLLECTION) {
            setVisible(true);
            handleCloseFullscreenPlayer();
        } else {
            setVisible(false);
        }
    }, [suspension]);

    const dialogConfig = useMemo(() => {
        switch (suspension) {
            case SUSPENSION_TYPES.VOLUNTARY:
                return {
                    title: SUSPENSION_VOLUNTARY_NOTIFICATION_TITLE,
                    description: SUSPENSION_VOLUNTARY_NOTIFICATION_BODY,
                    actionButtonTitle: TRY_AGAIN_BUTTON,
                    actionButtonOnPress: handleTryAgain,
                    errorCode: '403/20002',
                };
            case SUSPENSION_TYPES.COLLECTION:
                return {
                    title: collectionSuspensionActive
                        ? SUSPENSION_COLLECTION_NOTIFICATION_TITLE
                        : SUSPENSION_COLLECTION_OVER_NOTIFICATION_TITLE,
                    description: collectionSuspensionActive
                        ? SUSPENSION_COLLECTION_NOTIFICATION_BODY
                        : SUSPENSION_COLLECTION_OVER_NOTIFICATION_DESCRIPTION,
                    actionButtonTitle: SELFCARE_APP_BUTTON,
                    actionButtonOnPress: handleOpenSelfcareApp,
                    cancelButtonTitle: collectionSuspensionActive
                        ? RELOAD_BUTTON
                        : REBOOT_BUTTON,
                    cancelButtonOnPress: handleReloadApp,
                    cancelButtonBordered: true,
                    onRequestClose: () => {},
                };
            default:
                return {
                    title: '',
                };
        }
    }, [
        suspension,
        collectionSuspensionActive,
        handleTryAgain,
        handleOpenSelfcareApp,
        handleReloadApp,
    ]);

    const renderDescriptionComponent = useCallback(
        () => (
            <Text
                category={ETextCategory.body1}
                color={ETextColor.light_grey}
                style={styles.descriptionText}
                {...(isWeb && { tabIndex: 0 })}
            >
                {t(dialogConfig.description || '')}
            </Text>
        ),
        [dialogConfig.description],
    );

    const renderCloseComponent = useCallback(
        () =>
            loading ? (
                <View style={styles.loaderContainer}>
                    <Spinner
                        routeKey="suspension-alert"
                        size={isTabletOrWeb ? 48 : 32}
                    />
                </View>
            ) : null,
        [loading],
    );

    return (
        <SafeAreaView edges={edges}>
            <DialogView
                visible={visible}
                title={dialogConfig.title}
                DescriptionComponent={renderDescriptionComponent}
                CloseComponent={renderCloseComponent}
                actionButtonTitle={dialogConfig.actionButtonTitle}
                actionButtonOnPress={dialogConfig.actionButtonOnPress}
                actionButtonFillColor={COLOR.primary}
                actionButtonStyle={[
                    isTablet ? styles.tryAgainButtonTablet : styles.button,
                ]}
                actionButtonTextStyle={styles.buttonText}
                cancelButtonTitle={dialogConfig.cancelButtonTitle}
                cancelButtonOnPress={dialogConfig.cancelButtonOnPress}
                errorCode={dialogConfig.errorCode}
                cancelButtonBordered={dialogConfig.cancelButtonBordered}
                onRequestClose={dialogConfig.onRequestClose}
                style={styles.container}
            />
        </SafeAreaView>
    );
}
